import React, { useEffect, useState } from 'react';
import {
  login,
  getCurrentUser,
  logout,
} from '../modules/users/redux/operators';
import { useNavigate } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from '../shared/infra/redux';
import { useNotificationCenter } from '../shared/components/toast';
import { User } from '../modules/users/models/user';
import { CommunityModel } from '../modules/administrator/communities/models/Community.model';
import { getCommunityDetails } from '../modules/administrator/communities/redux/operators';
import { authService } from '../modules/users/services/';
import { LoadingModal } from '../shared/components/loader';
import { ReduxStatus } from '../shared/types';
import { resetAll } from '../shared/infra/redux/baseActions';

interface IAuthContext {
  isAuthenticated: boolean;
  isAdministrator: boolean;
  authUser: User | null;
  authCommunity: CommunityModel | null;
  handleLogin: (credential: string, password: string) => void;
  handleLogout: () => void;
  handleFetchCurrentUser: () => Promise<void>;
  loginStatus: ReduxStatus;
}

const AuthContext = React.createContext<IAuthContext | undefined>(undefined);
AuthContext.displayName = 'AuthContext';

interface IAuthProviderProps {
  children?: React.ReactNode;
}

const AuthProvider = ({ children }: IAuthProviderProps) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { addNotification } = useNotificationCenter();

  const [isInitialLoad, setIsInitialLoad] = useState<boolean>(false);

  const isReduxAuth = useAppSelector((state) => state.login.isAuthenticated);
  const loginStatus = useAppSelector((state) => state.login.loginStatus.status);

  const isAuthenticated = authService.isUserAuthenticated() && isReduxAuth;

  const isAdministrator = useAppSelector(
    (state) => state.login.isAdministrator,
  );

  const authUser = useAppSelector((state) => state.login.user);
  const authCommunity = useAppSelector((state) => state.community.community);

  const handleFetchCurrentUser = React.useCallback(async () => {
    const resultAction = await dispatch(getCurrentUser());
    if (getCurrentUser.fulfilled.match(resultAction)) {
      if (!resultAction.payload.isAdministratorUser) {
        await dispatch(getCommunityDetails());
      }
    }
  }, [dispatch]);

  // const handleLoginFetch = React.useCallback(async () => {
  //   const resultAction = await dispatch(getCurrentUser());
  //   if (getCurrentUser.fulfilled.match(resultAction)) {
  //     if (!resultAction.payload.isAdministratorUser) {
  //       await dispatch(getCommunityDetails());
  //     }
  //   }
  // }, [dispatch]);

  const handleLogout = React.useCallback(async () => {
    const resultAction = await dispatch(logout());
    if (logout.rejected.match(resultAction)) {
      navigate('/', { replace: true });
    }

    if (logout.fulfilled.match(resultAction)) {
      navigate('/', { replace: true });
      addNotification(
        {
          title: 'Success',
          description: 'User successfully logged out',
        },
        {
          variant: 'SUCCESS',
        },
      );
      dispatch(resetAll());
    }
  }, [addNotification, dispatch, navigate]);

  const handleLogin = React.useCallback(
    async (credential: string, password: string) => {
      const resultAction = await dispatch(login({ credential, password }));
      if (login.rejected.match(resultAction)) {
        addNotification(
          {
            title: 'Error',
            description: resultAction.payload?.message || '',
          },
          {
            variant: 'ERROR',
          },
        );
      }

      if (login.fulfilled.match(resultAction)) {
        addNotification(
          {
            title: 'Success',
            description: 'User successfully logged in',
          },
          {
            variant: 'SUCCESS',
          },
        );
        await handleFetchCurrentUser();
      }
    },
    [addNotification, dispatch, handleFetchCurrentUser],
  );

  useEffect(() => {
    async function fetchData() {
      try {
        await handleFetchCurrentUser();
        setIsInitialLoad(true);
      } catch (err) {
        setIsInitialLoad(true);
      }
    }

    void fetchData();
  }, [handleFetchCurrentUser]);

  const memoizedAuthValue = React.useMemo(
    () => ({
      authUser,
      authCommunity,
      isAuthenticated,
      isAdministrator,
      handleLogin,
      handleLogout,
      handleFetchCurrentUser,
      loginStatus,
    }),
    [
      isAuthenticated,
      handleLogin,
      handleLogout,
      authUser,
      authCommunity,
      isAdministrator,
      handleFetchCurrentUser,
      loginStatus,
    ],
  );

  return isInitialLoad ? (
    <AuthContext.Provider value={memoizedAuthValue}>
      {children}
    </AuthContext.Provider>
  ) : (
    <LoadingModal isLoading={true} />
  );
};

export const useAuth = (): IAuthContext => {
  const context = React.useContext(AuthContext);
  if (!context) {
    throw new Error(
      'This component must be used within a <AuthProvider> component',
    );
  }
  return context;
};

export { AuthProvider };
